import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  CardActionArea,
  CardContent,
  CardHeader,
  Chip,
  FormHelperText,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { ActionItemsContext } from "contexts/ActionItems";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { QuickSendDrawerContext } from "contexts/QuickSendDrawer";
import moment from "moment";
import { useContext } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { ActionItem, ActionItemType } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import StyledCard from "components/Styled/Card";
import OutreachContactDrawer from "features/Influencer/ContactList/OutreachContactDrawer";
import { fetcherAuth } from "utils/api";
import { formatEpochTimestamp } from "utils/time";
import { trackEvent } from "utils/tracking";

import DefaultActivityCard from "./ActivityCard";
import ListSkeleton from "./ListSkeleton";
import styles from "./styles";

interface Props {
  fetchLoading: boolean;
}

const ActionItemList = ({ fetchLoading }: Props) => {
  const navigate = useNavigate();
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const { handleOpenQuickSendIndividual } = useContext(QuickSendDrawerContext);
  const { actionItems, setActionItems } = useContext(ActionItemsContext);

  const handleArchive = async (e: CustomEvent, actionItem: ActionItem) => {
    e.stopPropagation();

    setActionItems((prev) => prev.filter((item) => item.id !== actionItem.id));
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/action-items/${actionItem.id}/archive`,
        "POST",
      );
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const openDrawer = (actionItem: ActionItem) => {
    trackEvent("Action Item Clicked", {
      "Action Item ID": actionItem.id,
      "Action Item Type": actionItem.type,
      "Outreach Contact ID": actionItem.outreachContactId,
    });
    if (
      actionItem.type === ActionItemType.new_contact_outreach &&
      actionItem?.outreachContact?.bentoBrandId
    ) {
      handleOpenQuickSendIndividual(
        actionItem?.outreachContact?.bentoBrandId,
        "Action Item",
        undefined,
        true,
      );
    } else {
      navigate(
        `action/${actionItem.outreachContactId}/email/${actionItem?.gmailThreadId}`,
      );
    }
  };

  const getDifference = (actionItem: ActionItem) => {
    const today = moment();
    const start =
      actionItem?.pendingAt ||
      actionItem?.outreachContact?.lastGmailMessageDate ||
      0;
    if (start === 0) return "";
    const lastEmailDate = moment(start * 1000);
    const duration = moment.duration(today.diff(lastEmailDate));
    const days = duration.asDays();

    if (days >= 30) {
      const month = duration.asMonths();

      return (
        `${month.toFixed(0)}` +
        (Number(month.toFixed(0)) < 2 ? ` month ago` : ` months ago`)
      );
    } else if (days >= 7) {
      const week = duration.asWeeks();
      return (
        `${week.toFixed(0)}` +
        (Number(week.toFixed(0)) < 2 ? ` week ago` : ` weeks ago`)
      );
    } else if (days >= 1) {
      return (
        `${days.toFixed(0)}` +
        (Number(days.toFixed(0)) < 2 ? ` day ago` : ` days ago`)
      );
    } else {
      return "";
    }
  };

  const buttonTitle = {
    [ActionItemType.past_partnership_check_in]: "Send Check-in",
    [ActionItemType.rate_discussion_follow_up]: "Send Follow-up",
    [ActionItemType.follow_up]: "Send Follow-up",
    [ActionItemType.new_contact_outreach]: "Send New Pitch",
    [ActionItemType.brand_response]: "Reply",
    [ActionItemType.out_of_office_follow_up]: "Send Follow-up",
  };

  if (fetchLoading) {
    return <ListSkeleton />;
  }
  return (
    <Box sx={{ mb: 3 }}>
      {actionItems?.length === 0 && <DefaultActivityCard />}
      {actionItems?.map((actionItem) => (
        <StyledCard key={actionItem.id} styles={styles.card}>
          <CardActionArea
            sx={{ px: 2, py: 1 }}
            onClick={() => openDrawer(actionItem)}
          >
            <CardHeader
              title={
                <Box sx={{ fontSize: { xs: 14, md: 18 } }}>
                  {actionItem?.brandName} | {actionItem?.description}
                  {getDifference(actionItem) && (
                    <Chip
                      label={getDifference(actionItem)}
                      sx={styles.dueChip}
                      size="small"
                    />
                  )}
                </Box>
              }
              subheader={
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography sx={{ fontSize: 14, color: "green.main" }}>
                    {actionItem?.outreachContact?.lastGmailMessageSubject}
                  </Typography>

                  <FormHelperText sx={{ color: "#AEBAFC", fontSize: 12 }}>
                    {formatEpochTimestamp({
                      epochTimestamp:
                        actionItem?.outreachContact?.lastGmailMessageDate || 0,
                      addYear: true,
                    })}{" "}
                  </FormHelperText>
                </Grid>
              }
              action={
                <IconButton
                  sx={styles.archiveButton}
                  onClick={(event) => handleArchive(event, actionItem)}
                >
                  <Box component="i" className="fa-regular fa-xmark" />
                </IconButton>
              }
            />

            <CardContent sx={{ mt: -3 }}>
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography sx={{ fontSize: 12, color: grey[500] }}>
                  {actionItem?.outreachContact?.lastGmailThreadParticipants}
                </Typography>
              </Grid>

              {actionItem?.outreachContact?.lastGmailMessageSnippet && (
                <Typography
                  component="div"
                  variant="caption"
                  color="textSecondary"
                  dangerouslySetInnerHTML={{
                    __html: `${actionItem?.outreachContact?.lastGmailMessageSnippet}...`,
                  }}
                  sx={styles.text}
                />
              )}

              <Button
                color="primary"
                variant="contained"
                sx={{ mt: 2 }}
                disableElevation
              >
                {buttonTitle[actionItem.type] || "Send Email"}
              </Button>
            </CardContent>
          </CardActionArea>
        </StyledCard>
      ))}

      <Routes>
        <Route
          path="action/:outreachContactId/*"
          element={<OutreachContactDrawer />}
        />
      </Routes>
    </Box>
  );
};

export default ActionItemList;
